import React from 'react';
import classNames from 'classnames';
import { Form, getFormValues, reduxForm } from 'redux-form';
import { Box, makeStyles, Typography } from '@material-ui/core';

import { connectTo } from '../../../utils';
import {
  email as emailValidation,
  phoneValidator
} from '../../../utils/validation';
import { parsePhoneFromString } from '../../../utils/phone';
import { useAppWidthClass } from '../../../utils/hooks';
import {
  EMAIL_FIELD,
  SMS_FIELD,
  WHATS_APP_FIELD
} from '../../../constants/response-channel';

import {
  ModalLayout,
  RadioGroup,
  ThemeButton,
  Translation
} from '../../common';
import { PaymentFormCheckbox } from '../../payments/index.troy';
import ResponseChannelFormField from '../ResponseChannelFormField';

const useStyles = makeStyles(() => ({
  form: {
    padding: '16px 48px'
  },
  formMobile: {
    padding: '16px 0 8px 0'
  },
  descriptionMobile: {
    padding: '0 8px'
  },
  group: {
    padding: '40px 25px 0 25px'
  },
  groupMobile: {
    padding: '32px 0 0 0'
  },
  row: {
    alignItems: 'flex-start',
    marginLeft: -8,
    marginRight: 0,

    '&:nth-of-type(1n+2)': {
      marginTop: 24
    }
  },
  rowMobile: {
    marginLeft: 0
  },
  radioIcon: {
    padding: 4,

    '& svg:first-of-type': {
      transform: 'scale(0.8)'
    }
  },
  radioLabel: {
    marginLeft: 8,
    width: '100%'
  },
  buttons: {
    marginTop: 40,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  buttonsMobile: {
    marginTop: 32,
    flexDirection: 'column',
    justifyContent: 'center'
  },
  submitMobile: {
    marginTop: 8,
    width: '100%',
    maxWidth: 300
  }
}));

const ResponseChannelForm = ({
  change,
  values,
  handleSubmit,
  onSubmit,
  isFileLoading,
  withWhatsapp
}) => {
  const classes = useStyles();
  const { isMobileWidth } = useAppWidthClass();

  const onTextFieldFocus = fieldName => e => {
    if (values.method !== fieldName) {
      change('method', fieldName);
    }
    e.preventDefault();
  };

  const isFieldDisabled = name => values.method !== name;

  const emailDisabled = isFieldDisabled(EMAIL_FIELD);
  const smsDisabled = isFieldDisabled(SMS_FIELD);
  const whatsAppDisabled = isFieldDisabled(WHATS_APP_FIELD);

  const formUntouched = emailDisabled && smsDisabled && whatsAppDisabled;

  const invalidEmail = !!(
    !emailDisabled && emailValidation(values[EMAIL_FIELD])
  );

  const invalidSms = !!(
    !smsDisabled && !!phoneValidator(values[SMS_FIELD].value)
  );

  const invalidWhatsapp = !!(
    !isFieldDisabled(WHATS_APP_FIELD) &&
    !!phoneValidator(values[WHATS_APP_FIELD].value)
  );

  const isFormInvalid =
    !values.method ||
    invalidEmail ||
    invalidSms ||
    invalidWhatsapp ||
    isFileLoading;

  return (
    <ModalLayout mobileHeaderMargin title="RESPONSE_CHANNEL_HEADER">
      <Form
        id="page.postbox.response-form"
        className={classNames(
          classes.form,
          isMobileWidth && classes.formMobile
        )}
        onSubmit={handleSubmit(({ remember, method, [method]: value }) =>
          onSubmit({
            remember: !!remember,
            method,
            value:
              method === SMS_FIELD || method === WHATS_APP_FIELD
                ? parsePhoneFromString(value.value).value
                : value
          })
        )}
      >
        <Typography
          className={classNames(isMobileWidth && classes.descriptionMobile)}
          variant="body1"
          color="textPrimary"
          align="center"
        >
          <Translation inline>{'RESPONSE_CHANNEL_DESCRIPTION'}</Translation>
        </Typography>
        <Box
          className={classNames(
            classes.group,
            isMobileWidth && classes.groupMobile
          )}
        >
          <RadioGroup
            name="method"
            radioClassName={classNames(
              classes.row,
              isMobileWidth && classes.rowMobile
            )}
            radioIconClassName={classes.radioIcon}
            radioLabelClassName={classes.radioLabel}
            options={[
              {
                label: (
                  <ResponseChannelFormField
                    disabled={emailDisabled}
                    formUntouched={formUntouched}
                    name={EMAIL_FIELD}
                    onClick={onTextFieldFocus}
                    invalid={invalidEmail}
                    invalidMessage="RESPONSE_CHANNEL_NEW_EMAIL"
                    header="E-Mail"
                    headerIcon="mailIcon"
                  />
                ),
                value: EMAIL_FIELD,
                id: 'page.postbox.component.response-channel-radiogroup.email',
                testLabelRole: 'response-channel-item'
              },
              {
                label: (
                  <ResponseChannelFormField
                    withPhoneInput
                    disabled={smsDisabled}
                    formUntouched={formUntouched}
                    name={SMS_FIELD}
                    onClick={onTextFieldFocus}
                    invalid={invalidSms}
                    invalidMessage="RESPONSE_CHANNEL_NEW_PHONE"
                    header="SMS"
                    headerIcon="contactChat"
                  />
                ),
                value: SMS_FIELD,
                id: 'page.postbox.component.response-channel-radiogroup.sms',
                testLabelRole: 'response-channel-item'
              },
              withWhatsapp && {
                label: (
                  <ResponseChannelFormField
                    withPhoneInput
                    disabled={whatsAppDisabled}
                    formUntouched={formUntouched}
                    name={WHATS_APP_FIELD}
                    onClick={onTextFieldFocus}
                    currentValue={values[WHATS_APP_FIELD]}
                    invalid={invalidWhatsapp}
                    invalidMessage="RESPONSE_CHANNEL_NEW_PHONE"
                    header="Whatsapp"
                    headerIcon="whatsappIcon"
                  />
                ),
                value: WHATS_APP_FIELD,
                id:
                  'page.postbox.component.response-channel-radiogroup.whatsapp',
                testLabelRole: 'response-channel-item'
              }
            ].filter(v => !!v)}
          />
          <Box
            id="page.postbox.response-form.save-changes-wrapper"
            className={classNames(
              classes.buttons,
              isMobileWidth && classes.buttonsMobile
            )}
          >
            <PaymentFormCheckbox
              id="page.postbox.component.response-channel.remember-checkbox"
              name="remember"
              label="RESPONSE_CHANNEL_REMEMBER"
              value={!!values.remember}
            />
            <ThemeButton
              className={classNames(isMobileWidth && classes.submitMobile)}
              id="page.postbox.component.response-channel.save-button"
              variant="contained"
              type="submit"
              size="large"
              disabled={isFormInvalid}
              textKey="RESPONSE_CHANNEL_CONFIRM"
              startIcon="confirm"
              startIconColor="primaryContrast"
            />
          </Box>
        </Box>
      </Form>
    </ModalLayout>
  );
};

const FORM_NAME = 'response-channel';

const connected = connectTo(
  state => ({
    values: getFormValues(FORM_NAME)(state)
  }),
  {},
  ResponseChannelForm
);

export default reduxForm({
  form: FORM_NAME,
  enableReinitialize: true
})(connected);
